import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { List, Menu, Button, Tooltip, Icon, Spacer, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { ability, ActionMap } from '@oliasoft/authorization-ui';
import { AccessSubjectTypes } from '@oliasoft/hierarchy-gui';
import type { IAccessControlModalsRef, IUser } from '@oliasoft/hierarchy-gui';

import { CaslEnums } from 'src/enums/casl-enums';
import { exportProject, groupedEstimates } from 'store/entities/projects/projects';
import { getItems, itemSelected, listCleaned, duplicateProject } from 'store/entities/hierarchy/hierarchy';
import { HierarchyLevelType } from 'src/enums/hierarchy';
import { ProjectCategoryType } from 'src/enums/projects';
import translations from 'src/internationalisation/translation-map.json';
import { routes } from 'routes/routes';
import { generatePath, navigateToPath } from 'store/navigation/navigation';
import { IDesign, IEstimate, ISelectedProject } from 'client/common/interfaces/hierarchy.interfaces.ts';
import { useListSorting } from 'client/common/sorting/use-list-sorting.tsx';
import { FetchingList } from 'client/common/lists/fetching-list.tsx';
import { validateEstimateAccess, hasRestrictedAccess } from 'client/common/access-control/estimate-access-validator.ts';
import { makeAccessControlOption } from 'src/common/hooks/use-access-control-menu';
import { getEntityAccessRightsList } from 'src/store/entities/access-control/access-control';
import type { TRootState } from 'src/store/store-types';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import { isEmpty } from 'lodash';

type EstimateListProps = PropsFromRedux & {
  companyId: string;
  currentUserId: string;
  estimates: IEstimate[];
  activeDesign: IDesign | null;
  isEstimateFetching: boolean;
  addButtonDisabled: boolean;
  onClickDelete: (id: string, type: HierarchyLevelType) => void;
  itemModalVisibleUpdated: (data: { type: HierarchyLevelType; value: boolean }) => void;
  setEditItem: (data: IEstimate | null) => void;
  onUpdateProjectCategory: (id: string, category: string) => void;
  selectedProject: ISelectedProject;
  accessControlChildRef: React.RefObject<IAccessControlModalsRef | null>;
  users: IUser[];
  apiKeys?: string[];
  getEntityAccessRightsList: (type: string, id: string, subjectType: AccessSubjectTypes) => void;
};

const EstimateList = ({
  companyId,
  estimates,
  activeDesign,
  onClickDelete,
  itemModalVisibleUpdated,
  setEditItem,
  navigateToPath,
  getItems,
  itemSelected,
  listCleaned,
  addButtonDisabled,
  exportProject,
  isEstimateFetching,
  duplicateProject,
  onUpdateProjectCategory,
  currentUserId,
  users,
  apiKeys,
  entityAccessRightsList,
  getEntityAccessRightsList,
  accessControlChildRef,
  selectedProject,
}: EstimateListProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;
    if (activeDesign?.designid) {
      const fetchData = async () => {
        await getItems(companyId, HierarchyLevelType.Estimate, activeDesign.designid);
        if (!cancelled && !isEmpty(selectedProject.estimateId)) {
          itemSelected({
            id: selectedProject.estimateId,
            type: HierarchyLevelType.Estimate,
            clearChildren: false,
          });
        }
      };
      fetchData();
    }

    return () => {
      cancelled = true;
      listCleaned(HierarchyLevelType.Estimate);
    };
  }, [companyId, activeDesign, selectedProject.estimateId]);

  useEffect(() => {
    accessControlChildRef.current?.accessRightListUpdated({
      accessRightsList: entityAccessRightsList,
      metadata: [],
    });
  }, [entityAccessRightsList]);

  const { items, sortAction } = useListSorting(estimates);

  if (isEstimateFetching) {
    return <FetchingList testId="estimate-list" name={t(translations.estimate)} />;
  }

  const listItems: any = [];
  const categoryLabels = {
    [ProjectCategoryType.Actual]: t(translations.actual),
    [ProjectCategoryType.Prototype]: t(translations.prototype),
  };

  const projectCategoriesMap = {
    [ProjectCategoryType.Actual]: {
      label: t(translations.prototype),
      category: ProjectCategoryType.Prototype,
    },
    [ProjectCategoryType.Prototype]: {
      label: t(translations.actual),
      category: ProjectCategoryType.Actual,
    },
  };
  const userHasAdminAccess = ability.can(ActionMap.UPDATE, CaslEnums.ManageAccessRights);

  const getEstimateEditData = (estimate: IEstimate): Partial<IEstimate> => {
    const { estimateid, designid, name, category, design, companyid } = estimate;

    return {
      estimateid,
      designid,
      name,
      category,
      design,
      companyid,
    };
  };

  const listItemFromEstimate = (estimate: IEstimate) => {
    const { estimateid: estimateId, accessMetadata } = estimate;
    const isEstimateAccessible = validateEstimateAccess(currentUserId, estimate);
    const accessManager = users.find((user) => user.subjectId === accessMetadata?.restrictedby);
    const accessManagerName = `${accessManager?.firstName} ${accessManager?.lastName}`;

    const details = (
      <>
        {!isEstimateAccessible && (
          <Tooltip text={t(translations.accessControl_pleaseRequestAccessFromUser)} display="inline-block">
            <Icon icon="lock" color="var(--color-text-error)" />
            <Spacer width="7px" />
            {t(translations.accessControl_lockedByAccessManagerName, {
              accessManagerName,
            })}
          </Tooltip>
        )}
        {hasRestrictedAccess(estimate) && isEstimateAccessible && (
          <>
            <Icon icon="unlock" color="var(--color-text-success)" />
            <Spacer width="7px" />
            {t(translations.accessControl_accessRestrictedByAccessManagerName, {
              accessManagerName,
            })}
          </>
        )}
      </>
    );

    return {
      testId: `estimate-list-${convertToKebabCase(estimate.name)}-item`,
      id: estimateId,
      name: estimate.name,
      details,
      metadata: estimate.design,
      active: estimate.active,
      onClick: () => {
        itemSelected({
          type: HierarchyLevelType.Estimate,
          id: estimateId,
        });

        navigateToPath(
          generatePath(routes.overview.fullPath, {
            company: estimate.companyid,
            project: estimateId,
          }),
        );
      },
      actions: [
        {
          testId: `estimate-list-${convertToKebabCase(estimate.name)}-more-button`,
          childComponent: (
            <Menu
              menu={{
                label: t(translations.more),
                sections: [
                  {
                    label: t(translations.update),
                    icon: 'edit',
                    type: 'Option',
                    onClick: () => {
                      setEditItem(getEstimateEditData(estimate) as IEstimate);
                      itemModalVisibleUpdated({
                        type: HierarchyLevelType.Estimate,
                        value: true,
                      });
                    },
                  },
                  {
                    testId: `estimate-list-${convertToKebabCase(estimate.name)}-duplicate-option`,
                    label: t(translations.duplicate),
                    icon: 'duplicate',
                    type: 'Option',
                    onClick: () => {
                      duplicateProject(getEstimateEditData(estimate) as IEstimate);
                    },
                  },
                  {
                    type: 'Divider',
                  },
                  {
                    label: t(translations.downloadAsJson),
                    icon: 'download',
                    type: 'Option',
                    onClick: () => exportProject(estimateId, estimate.name),
                  },
                  ...(estimate.subjectId === currentUserId || userHasAdminAccess
                    ? [
                        {
                          type: 'Divider',
                        },
                        makeAccessControlOption({
                          t,
                          item: {
                            ...estimate,
                            id: estimateId,
                            entityType: HierarchyLevelType.Estimate,
                          },
                          subjectType: AccessSubjectTypes.User,
                          icon: 'key',
                          accessControlChildRef,
                          users,
                          apiKeys,
                          getEntityAccessRightsList,
                        }),
                      ]
                    : []),
                  {
                    type: 'Divider',
                  },
                  {
                    testId: `estimate-list-${convertToKebabCase(estimate.name)}-delete-option`,
                    label: t(translations.delete),
                    icon: 'delete',
                    type: 'Option',
                    onClick: () => onClickDelete(estimateId, HierarchyLevelType.Estimate),
                  },
                  {
                    type: 'Divider',
                  },
                  {
                    label: t(translations.projects_setAsIdentifier, {
                      identifier: projectCategoriesMap[estimate.category as ProjectCategoryType].label,
                    }),
                    type: 'Option',
                    onClick: () =>
                      onUpdateProjectCategory(
                        estimateId,
                        projectCategoriesMap[estimate.category as ProjectCategoryType].category,
                      ),
                  },
                ],
                trigger: 'Component',
                component: <Button small basic round colored="muted" icon="menu" />,
              }}
            />
          ),
        },
      ],
    };
  };

  const estimateGroups: any = groupedEstimates(items);

  Object.values(ProjectCategoryType).forEach((category) => {
    const estimateGroup = estimateGroups[category];
    if (!estimateGroup) {
      return;
    }
    listItems.push({
      id: `heading-${category}`,
      name: categoryLabels[category],
      type: 'Heading',
      metaCount: estimateGroup.length,
    });
    listItems.push(...estimateGroup.map(listItemFromEstimate));
  });

  return (
    <List
      testId="estimate-list"
      list={{
        name: t(translations.estimate),
        actions: [
          sortAction,
          {
            // @ts-ignore
            testId: 'estimate-list-subheading-add-button',
            label: 'Add',
            icon: 'add',
            onClick: () =>
              itemModalVisibleUpdated({
                type: HierarchyLevelType.Estimate,
                value: true,
              }),
            disabled: addButtonDisabled || isEstimateFetching,
          },
        ],
        items: listItems,
      }}
      stickyHeader
      expanding
      scrollDetails={{
        scrollable: true,
        hideScrollbar: true,
      }}
    />
  );
};

const mapStateToProps = ({ entities }: TRootState) => ({
  apiKeys: entities?.accessControl.apiKeys,
  users: entities?.user.users,
  entityAccessRightsList: entities?.accessControl.accessRights,
});

const mapDispatchToProps = {
  navigateToPath,
  exportProject,
  duplicateProject,
  getItems,
  itemSelected,
  listCleaned,
  getEntityAccessRightsList,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(EstimateList));

export { Container as EstimateList };
